import { useRef, useState } from 'react';
import { api, setToken } from '../api.js';

/**
 * §2 Landing.
 *
 * Copy, markup and class names are the vanilla shell's, unchanged. The one
 * deliberate departure from the design prototype is the same one the vanilla
 * app makes and spec §2.5 calls out itself: the prototype DISPLAYS a
 * reference code because it already knows who the visitor is; production has
 * to ASK for one. The row is an input styled to the prototype's
 * reference-code treatment.
 *
 * Two of §2.5's four error states are handled, as before: not recognised,
 * and rate-limited. The client never decides what a code means — it posts it
 * and the server answers with a role.
 */
export default function Landing({ onSignedIn, adminEntry }) {
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  async function submit(event) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    try {
      const session = await api('/api/session', { method: 'POST', body: { accessCode: code } });
      setToken(session.token);
      // The typed code, not anything echoed back: the API deliberately never
      // returns an access code. §1.5 wants it in the header's pill.
      onSignedIn(session, code.trim().toUpperCase());
    } catch (err) {
      setError(err.status === 429
        ? 'Too many attempts. Please wait a few minutes and try again.'
        : 'That reference was not recognised. Check the code on your invitation.');
      setBusy(false);
      inputRef.current?.focus();
    }
  }

  return (
    // `view` only: the stylesheet hides inactive views with the `hidden`
    // attribute, so there is no `.active` class to set. React renders one
    // view at a time, so nothing needs hiding.
    <section className="view">
      <div className="landing-main">
        <div className="landing-col">
          <h1 className="hero-headline">
            {adminEntry ? 'Fáilte Ireland staff sign-in' : "Let's measure your digital maturity"}
          </h1>
          <p className="hero-subtext">
            {adminEntry
              ? 'Enter your staff reference to see the companies assigned to you.'
              : 'A short assessment of your digital and AI capability — and where Fáilte Ireland can help.'}
          </p>

          <form onSubmit={submit} autoComplete="off">
            <div className="ref-row">
              <label className="ref-label" htmlFor="access-code">
                {adminEntry ? 'Enter your staff reference' : 'Enter your assessment reference'}
              </label>
              <input
                className="ref-input"
                id="access-code"
                name="accessCode"
                type="text"
                placeholder={adminEntry ? 'FI-ADMIN-XXXXX' : 'FI-TIER1-XXXXX'}
                spellCheck="false"
                autoCapitalize="characters"
                autoComplete="off"
                required
                aria-describedby="ref-helper"
                aria-invalid={error ? 'true' : 'false'}
                ref={inputRef}
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
            </div>

            <p id="ref-helper" className="ref-helper">
              Assigned by Fáilte Ireland — no password needed. Your answers feed anonymised
              national insights, and you&apos;ll confirm consent before you finish.
            </p>

            {error && <p className="login-error" role="alert">{error}</p>}

            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Checking…' : (
                <>
                  Begin assessment{' '}
                  <span aria-hidden="true" style={{ fontSize: '15px' }}>→</span>
                </>
              )}
            </button>

            <div className="meta-line">About 25–35 minutes &nbsp;·&nbsp; Save and return any time</div>
          </form>
        </div>
      </div>
    </section>
  );
}
